import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { MapPinIcon, StarIcon } from "@heroicons/react/24/outline";
import { DotMatrixSVG } from "@/components/SVGElements";

const locations = [
  "Delhi",
  "Gurugram",
  "Noida",
  "Faridabad",
  "Ghaziabad",
  "Agra",
  "Mathura",
  "Bhopal",
  "Vrindavan",
  "Palwal",
];

const testimonials = [
  {
    quote: "Visioner Consultants delivered exceptional results for our commercial complex project. Their attention to detail and commitment to timelines made them the perfect partner for our vision.",
    author: "Rajesh Kumar",
    company: "Kumar Developers",
    location: "Gurugram",
  },
  {
    quote: "The structural engineering team at Visioner is outstanding. They provided innovative solutions that saved us both time and cost while maintaining the highest quality standards.",
    author: "Priya Sharma",
    company: "Sharma Constructions",
    location: "Delhi",
  },
  {
    quote: "We've worked with many consultants, but Visioner stands out for their professionalism and expertise. The turnkey solution they delivered exceeded our expectations.",
    author: "Amit Gupta",
    company: "Gupta Enterprises",
    location: "Noida",
  },
];

const stats = [
  { number: "150+", label: "Projects Delivered" },
  { number: "100+", label: "Happy Clients" },
  { number: "50+", label: "Team Members" },
  { number: "12+", label: "Years Experience" },
];

const ClientsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-zinc-50"
    >
      <Navbar />
      <main className="pt-20">
        {/* Page Header */}
        <section className="py-20 bg-slate-900 relative overflow-hidden">
          <div className="absolute inset-0 text-white">
            <DotMatrixSVG />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                Our Reach
              </span>
              <h1 className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4">
                Trusted Across <span className="text-orange-500">10+ Cities</span>
              </h1>
              <p className="text-slate-300 mt-6 text-lg font-manrope max-w-3xl mx-auto">
                Our expertise spans across multiple cities in North India, delivering
                excellence to diverse clients and projects.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <p className="font-oswald text-4xl sm:text-5xl font-bold text-slate-900">
                    {stat.number.replace("+", "")}
                    <span className="text-orange-500">+</span>
                  </p>
                  <p className="text-slate-600 mt-2 font-manrope">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Locations */}
        <section className="py-20 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                Our Presence
              </span>
              <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                Cities We Serve
              </h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4"
            >
              {locations.map((location, index) => (
                <motion.div
                  key={location}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-center gap-2 bg-white px-4 py-4 border border-slate-200 hover:border-orange-500/50 hover:shadow-md transition-all"
                >
                  <MapPinIcon className="w-5 h-5 text-orange-500" />
                  <span className="text-slate-900 font-manrope font-medium">
                    {location}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                Testimonials
              </span>
              <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                What Our Clients Say
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-zinc-50 border border-slate-100 p-8"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} className="w-5 h-5 text-orange-500 fill-orange-500" />
                    ))}
                  </div>
                  <p className="text-slate-600 font-manrope leading-relaxed mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-semibold text-slate-900 font-manrope">
                      {testimonial.author}
                    </p>
                    <p className="text-sm text-slate-500 font-manrope">
                      {testimonial.company}, {testimonial.location}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-white mb-6">
              Join Our Growing List of <span className="text-orange-500">Happy Clients</span>
            </h2>
            <p className="text-slate-300 font-manrope mb-8">
              Experience the Visioner difference. Let's build something amazing together.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-orange-500 text-white px-8 py-4 font-manrope font-semibold uppercase tracking-wider text-sm hover:bg-orange-600 transition-colors"
              data-testid="clients-cta"
            >
              Get in Touch
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
};

export default ClientsPage;
